import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ScrollText, CheckCircle, XCircle } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { Modal } from '../components/Modal'
import { Select, SelectItem } from '../components/ui/select'
import { useAuth } from '../store/auth'
import { getAuthAuditLogs } from '../api/auth'
import { getMailAuditLogs } from '../api/mail'
import { getStorageAuditLogs } from '../api/storage'
import { getBrainerAuditLogs } from '../api/brainer'
import { getInvoiceFacteurAuditLogs } from '../api/invoice-facteur'
import { getOrderParserAuditLogs } from '../api/order-parser'
import { getBuzzAuditLogs } from '../api/buzz'
import { getEnergyOsAuditLogs } from '../api/energy-os'
import { EmptyState } from '../components/EmptyState'
import { useDateFmt } from '../hooks/useDateFmt'
import type { AuthAuditLog, MailAuditLog, StorageAuditLog, BrainerAuditLog, InvoiceFacteurAuditLog, OrderParserAuditLog, BuzzAuditLog, EnergyOsAuditLog, ModuleKey } from '../types'

type Tab = 'all' | 'auth' | 'mail' | 'storage' | 'brainer-bo' | 'brainer-fo' | 'invoice-facteur' | 'order-parser' | 'buzz' | 'energy-os'

const FO_TYPES = new Set(['coffee', 'journey'])

function StatusIcon({ success }: { success: boolean }) {
  return success
    ? <CheckCircle size={13} className="text-emerald-500 shrink-0" />
    : <XCircle size={13} className="text-red-500 shrink-0" />
}

const SERVICE_BADGE: Record<string, string> = {
  auth:        'bg-blue-50 text-blue-700',
  mail:        'bg-purple-50 text-purple-700',
  storage:     'bg-amber-50 text-amber-700',
  'brainer-bo': 'bg-green-50 text-green-700',
  'brainer-fo': 'bg-teal-50 text-teal-700',
  'invoice-facteur': 'bg-rose-50 text-rose-700',
  'order-parser': 'bg-indigo-50 text-indigo-700',
  buzz:        'bg-orange-50 text-orange-700',
  'energy-os': 'bg-lime-50 text-lime-700',
}
const SERVICE_LABEL: Record<string, string> = {
  auth:        'Auth',
  mail:        'Mail',
  storage:     'Storage',
  'brainer-bo': 'Builder',
  'brainer-fo': 'Records',
  'invoice-facteur': 'Invoice Facteur',
  'order-parser': 'Order Parser',
  buzz:        'Buzz',
  'energy-os': 'EnergyOS',
}

type NormalizedLog = {
  id: string
  service: Tab
  timestamp: string
  action: string
  actor?: string
  resourceType?: string
  success: boolean
  details?: Record<string, unknown>
}

function normalize(logs: {
  auth: AuthAuditLog[]
  mail: MailAuditLog[]
  storage: StorageAuditLog[]
  brainerAll: BrainerAuditLog[]
  invoiceFacteur: InvoiceFacteurAuditLog[]
  orderParser: OrderParserAuditLog[]
  buzz: BuzzAuditLog[]
  energyOs: EnergyOsAuditLog[]
}): NormalizedLog[] {
  const out: NormalizedLog[] = []

  for (const l of logs.auth) {
    out.push({ id: l.id, service: 'auth', timestamp: l.timestamp, action: l.action, actor: l.userId, resourceType: l.resourceType, success: l.success, details: l.details })
  }
  for (const l of logs.mail) {
    out.push({ id: l.id, service: 'mail', timestamp: l.timestamp, action: l.action, actor: l.mailboxId, resourceType: undefined, success: l.success, details: l.details })
  }
  for (const l of logs.storage) {
    out.push({ id: l.id, service: 'storage', timestamp: l.timestamp, action: l.action, actor: l.serviceId, resourceType: undefined, success: l.success, details: l.details })
  }
  for (const l of logs.brainerAll) {
    const svc = FO_TYPES.has(l.resourceType ?? '') ? 'brainer-fo' : 'brainer-bo'
    out.push({ id: l.id, service: svc as Tab, timestamp: l.createdAt, action: l.action, actor: l.userId, resourceType: l.resourceType, success: l.success, details: l.details })
  }
  for (const l of logs.invoiceFacteur) {
    out.push({ id: l.id, service: 'invoice-facteur', timestamp: l.timestamp, action: l.action, actor: l.invoiceId, resourceType: undefined, success: l.success, details: l.details })
  }
  for (const l of logs.orderParser) {
    out.push({ id: l.id, service: 'order-parser', timestamp: l.timestamp, action: l.action, actor: l.documentId, resourceType: undefined, success: l.success, details: l.details })
  }
  for (const l of logs.buzz) {
    out.push({ id: l.id, service: 'buzz', timestamp: l.timestamp, action: l.action, actor: l.serviceName, resourceType: l.resourceId, success: l.success, details: l.details })
  }
  for (const l of logs.energyOs) {
    out.push({ id: l.id, service: 'energy-os', timestamp: l.timestamp, action: l.action, actor: l.homeId ?? l.deviceId, resourceType: l.deviceId ? 'device' : (l.homeId ? 'home' : undefined), success: l.success, details: l.details })
  }

  return out.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}

export function AuditLogs() {
  const { user: me } = useAuth()
  const businessId = me!.businessId
  const fmt = useDateFmt()
  const [tab, setTab] = useState<Tab>('auth')
  const [limit, setLimit] = useState('100')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<NormalizedLog | null>(null)

  const isBrainer = tab === 'brainer-bo' || tab === 'brainer-fo' || tab === 'all'
  const limitN = parseInt(limit)

  const authQ = useQuery({
    queryKey: ['audit-auth', businessId, limit],
    queryFn: () => getAuthAuditLogs({ businessId, limit: limitN }).then((r) => r.data),
    enabled: tab === 'auth' || tab === 'all',
  })
  const mailQ = useQuery({
    queryKey: ['audit-mail', limit],
    queryFn: () => getMailAuditLogs({ limit: limitN }).then((r) => r.data),
    enabled: tab === 'mail' || tab === 'all',
  })
  const storageQ = useQuery({
    queryKey: ['audit-storage', businessId, limit],
    queryFn: () => getStorageAuditLogs({ businessId, limit: limitN }).then((r) => r.data),
    enabled: tab === 'storage' || tab === 'all',
  })
  const brainerQ = useQuery({
    queryKey: ['audit-brainer', businessId, limit],
    queryFn: () => getBrainerAuditLogs({ limit: limitN }).then((r) => r.data.rows),
    enabled: isBrainer,
  })
  const invoiceFacteurQ = useQuery({
    queryKey: ['audit-invoice-facteur', limit],
    queryFn: () => getInvoiceFacteurAuditLogs({ limit: limitN }).then((r) => r.data),
    enabled: tab === 'invoice-facteur' || tab === 'all',
  })
  const orderParserQ = useQuery({
    queryKey: ['audit-order-parser', limit],
    queryFn: () => getOrderParserAuditLogs({ limit: limitN }).then((r) => r.data),
    enabled: tab === 'order-parser' || tab === 'all',
  })
  const buzzQ = useQuery({
    queryKey: ['audit-buzz', limit],
    queryFn: () => getBuzzAuditLogs({ limit: limitN }).then((r) => r.data),
    enabled: tab === 'buzz' || tab === 'all',
  })
  const energyOsQ = useQuery({
    queryKey: ['audit-energy-os', limit],
    queryFn: () => getEnergyOsAuditLogs({ limit: limitN }).then((r) => r.data),
    enabled: tab === 'energy-os' || tab === 'all',
  })

  const allTabs: { key: Tab; label: string; moduleKey?: ModuleKey }[] = [
    { key: 'all',        label: 'All' },
    { key: 'auth',       label: 'Identity & Access' },
    { key: 'mail',       label: 'Mail', moduleKey: 'mail-service' },
    { key: 'storage',    label: 'Storage', moduleKey: 'storage-service' },
    { key: 'brainer-bo', label: 'Brainer · Backoffice', moduleKey: 'brainer' },
    { key: 'brainer-fo', label: 'Brainer · Frontoffice', moduleKey: 'brainer' },
    { key: 'invoice-facteur', label: 'Invoice Facteur', moduleKey: 'invoice-facteur' },
    { key: 'order-parser', label: 'Order Parser', moduleKey: 'order-parser' },
    { key: 'buzz',       label: 'Buzz' },
    { key: 'energy-os',  label: 'EnergyOS', moduleKey: 'energy-os' },
  ]
  const tabs = allTabs.filter((t) => !t.moduleKey || me?.enabledModules?.includes(t.moduleKey))

  const isLoading =
    tab === 'auth'       ? authQ.isLoading :
    tab === 'mail'       ? mailQ.isLoading :
    tab === 'storage'    ? storageQ.isLoading :
    tab === 'brainer-bo' ? brainerQ.isLoading :
    tab === 'brainer-fo' ? brainerQ.isLoading :
    tab === 'invoice-facteur' ? invoiceFacteurQ.isLoading :
    tab === 'order-parser' ? orderParserQ.isLoading :
    tab === 'buzz' ? buzzQ.isLoading :
    tab === 'energy-os' ? energyOsQ.isLoading :
    authQ.isLoading || mailQ.isLoading || storageQ.isLoading || brainerQ.isLoading || invoiceFacteurQ.isLoading || orderParserQ.isLoading || buzzQ.isLoading || energyOsQ.isLoading

  const brainerLogs = brainerQ.data ?? []
  const boLogs = brainerLogs.filter((l) => !FO_TYPES.has(l.resourceType ?? ''))
  const foLogs = brainerLogs.filter((l) => FO_TYPES.has(l.resourceType ?? ''))

  const allNormalized = tab === 'all'
    ? normalize({
        auth: authQ.data ?? [], mail: mailQ.data ?? [], storage: storageQ.data ?? [],
        brainerAll: brainerQ.data ?? [], invoiceFacteur: invoiceFacteurQ.data ?? [],
        orderParser: orderParserQ.data ?? [], buzz: buzzQ.data ?? [], energyOs: energyOsQ.data ?? [],
      })
    : []

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Audit Logs</h1>
          <p className="text-sm text-ink-dim mt-0.5">Track all activity across your services</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search logs" />
          <Select value={limit} onValueChange={setLimit} className="w-32">
            {['50', '100', '200', '500'].map((n) => <SelectItem key={n} value={n}>Last {n}</SelectItem>)}
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex mb-4 border-b border-paper-border">
        {tabs.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : tab === 'auth' ? (
          <AuthTable logs={authQ.data ?? []} search={search} onSelect={setSelected} />
        ) : tab === 'mail' ? (
          <MailTable logs={mailQ.data ?? []} search={search} onSelect={setSelected} />
        ) : tab === 'storage' ? (
          <StorageTable logs={storageQ.data ?? []} search={search} onSelect={setSelected} />
        ) : tab === 'brainer-bo' ? (
          <BrainerTable logs={boLogs} search={search} label="back-office" onSelect={setSelected} />
        ) : tab === 'brainer-fo' ? (
          <BrainerTable logs={foLogs} search={search} label="front-office" onSelect={setSelected} />
        ) : tab === 'invoice-facteur' ? (
          <InvoiceFacteurTable logs={invoiceFacteurQ.data ?? []} search={search} onSelect={setSelected} />
        ) : tab === 'order-parser' ? (
          <OrderParserTable logs={orderParserQ.data ?? []} search={search} onSelect={setSelected} />
        ) : tab === 'buzz' ? (
          <BuzzTable logs={buzzQ.data ?? []} search={search} onSelect={setSelected} />
        ) : tab === 'energy-os' ? (
          <EnergyOsTable logs={energyOsQ.data ?? []} search={search} onSelect={setSelected} />
        ) : (
          <AllTable logs={allNormalized} search={search} onSelect={setSelected} />
        )}
      </div>

      {selected && (
        <Modal title={selected.action} onClose={() => setSelected(null)} size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1">Timestamp</p>
                <p className="text-ink">{fmt(selected.timestamp)}</p>
              </div>
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1">Status</p>
                <div className="flex items-center gap-1.5">
                  <StatusIcon success={selected.success} />
                  <span className="text-ink">{selected.success ? 'Success' : 'Failed'}</span>
                </div>
              </div>
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1">Service</p>
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${SERVICE_BADGE[selected.service]}`}>
                  {SERVICE_LABEL[selected.service]}
                </span>
              </div>
              {selected.actor && (
                <div>
                  <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1">Actor</p>
                  <p className="font-mono text-xs text-ink break-all">{selected.actor}</p>
                </div>
              )}
              {selected.resourceType && (
                <div>
                  <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1">Resource</p>
                  <p className="text-ink">{selected.resourceType}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">Details</p>
              <pre className="bg-paper-sunken border border-paper-border rounded-md px-4 py-3 text-xs font-mono text-ink overflow-x-auto max-h-96 scrollbar-thin">
                {Object.keys(selected.details ?? {}).length > 0 ? JSON.stringify(selected.details, null, 2) : '{}'}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── Auth table ─────────────────────────────────────────────────────────────────

function AuthTable({ logs, search, onSelect }: { logs: AuthAuditLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.userId ?? '', l.resourceType ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No auth audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>User ID</th>
          <th>Resource</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: 'auth', timestamp: l.timestamp, action: l.action, actor: l.userId, resourceType: l.resourceType, success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.timestamp)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.userId ?? ''}</td>
            <td className="text-xs text-ink-dim">{l.resourceType ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {Object.keys(l.details ?? {}).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Mail table ─────────────────────────────────────────────────────────────────

function MailTable({ logs, search, onSelect }: { logs: MailAuditLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.mailboxId ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No mail audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>Mailbox ID</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: 'mail', timestamp: l.timestamp, action: l.action, actor: l.mailboxId, resourceType: undefined, success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.timestamp)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.mailboxId ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Storage table ──────────────────────────────────────────────────────────────

function StorageTable({ logs, search, onSelect }: { logs: StorageAuditLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.serviceId ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No storage audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>Service ID</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: 'storage', timestamp: l.timestamp, action: l.action, actor: l.serviceId, resourceType: undefined, success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.timestamp)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.serviceId ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Invoice Facteur table ───────────────────────────────────────────────────────

function InvoiceFacteurTable({ logs, search, onSelect }: { logs: InvoiceFacteurAuditLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.invoiceId ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No invoice facteur audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>Invoice ID</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: 'invoice-facteur', timestamp: l.timestamp, action: l.action, actor: l.invoiceId, resourceType: undefined, success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.timestamp)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.invoiceId ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Order parser table ──────────────────────────────────────────────────────────

function OrderParserTable({ logs, search, onSelect }: { logs: OrderParserAuditLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.documentId ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No order parser audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>Document ID</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: 'order-parser', timestamp: l.timestamp, action: l.action, actor: l.documentId, resourceType: undefined, success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.timestamp)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.documentId ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Buzz table ───────────────────────────────────────────────────────────────

function BuzzTable({ logs, search, onSelect }: { logs: BuzzAuditLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.serviceName ?? '', l.resourceId ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No buzz audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>Service Name</th>
          <th>Resource ID</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: 'buzz', timestamp: l.timestamp, action: l.action, actor: l.serviceName, resourceType: l.resourceId, success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.timestamp)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.serviceName ?? ''}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.resourceId ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── EnergyOS table ───────────────────────────────────────────────────────────

function EnergyOsTable({ logs, search, onSelect }: { logs: EnergyOsAuditLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.homeId ?? '', l.deviceId ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No EnergyOS audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>Home ID</th>
          <th>Device ID</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: 'energy-os', timestamp: l.timestamp, action: l.action, actor: l.homeId ?? l.deviceId, resourceType: l.deviceId ? 'device' : (l.homeId ? 'home' : undefined), success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.timestamp)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.homeId ?? ''}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.deviceId ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Brainer table (shared for backoffice + frontoffice) ────────────────────────

function BrainerTable({ logs, search, label, onSelect }: { logs: BrainerAuditLog[]; search: string; label: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.userId ?? '', l.resourceType ?? ''].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title={`No ${label} audit logs`} />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Action</th>
          <th>User ID</th>
          <th>Resource</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}
            onClick={() => onSelect({ id: l.id, service: FO_TYPES.has(l.resourceType ?? '') ? 'brainer-fo' : 'brainer-bo', timestamp: l.createdAt, action: l.action, actor: l.userId, resourceType: l.resourceType, success: l.success, details: l.details })}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim">{fmt(l.createdAt)}</td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.userId ?? ''}</td>
            <td className="text-xs text-ink-dim">{l.resourceType ?? ''}</td>
            <td className="text-xs text-ink-dim truncate max-w-xs">
              {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── All table ──────────────────────────────────────────────────────────────────

function AllTable({ logs, search, onSelect }: { logs: NormalizedLog[]; search: string; onSelect: (l: NormalizedLog) => void }) {
  const fmt = useDateFmt()
  const s = search.toLowerCase()
  const rows = s ? logs.filter((l) => [l.action, l.actor ?? '', l.resourceType ?? '', SERVICE_LABEL[l.service]].some((v) => v.toLowerCase().includes(s))) : logs
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="No audit logs" />
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-6" />
          <th>Timestamp</th>
          <th>Service</th>
          <th>Action</th>
          <th>Actor</th>
          <th>Resource</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={`${l.service}-${l.id}`}
            onClick={() => onSelect(l)}
            className="cursor-pointer">
            <td><StatusIcon success={l.success} /></td>
            <td className="text-ink-dim text-xs">{fmt(l.timestamp)}</td>
            <td>
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${SERVICE_BADGE[l.service]}`}>
                {SERVICE_LABEL[l.service]}
              </span>
            </td>
            <td className="font-mono text-xs">{l.action}</td>
            <td className="font-mono text-xs text-ink-dim truncate max-w-[8rem]">{l.actor ?? ''}</td>
            <td className="text-xs text-ink-dim">{l.resourceType ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
